/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColDef} from 'ag-grid-community';
import isArray from 'lodash.isarray';
import isNil from 'lodash.isnil';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {CTCEntity, GenericObject} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {ValidateRequestResults} from './IonGridUtils';

// defines the type definitions for export columns ...
export type ColumnDef = {
  headerName?: string;
  key?: string;
};

// defines the default newline token used when generating a csv ...
const LINE_SEPARATOR   = '\r\n';

// defines the default column separator string used when generating a csv ...
const COLUMN_SEPARATOR = ',';

/**
 * Defines the shape of the client export parameters which are generally used by
 * the IonGrid when invoking getExportParams on the grid ref.
 */
export interface ExportClient {
  columns: ColDef[];
  augmenterArgs?: any[] | GenericObject;
  sortOn?: any;
  filterOn?: any;
  augmenter?: Function;
  exportLimit?: number;
  includeSubGroups?: boolean;
  items?: CTCEntity[];
  queryString?: string;
  largeFleetAssetCount?: number | null;
  gridExportCount?: number;
  areTotalRowsSelected?: boolean;
  includeSortParams?: boolean;
  includeParams?: boolean;
  maxSearch?: boolean;
  pageSize?: number;
  showInventoryOnly?: boolean;
  includeParent?: boolean;
  requestValidations?: ValidateRequestResults;
  excludeInheritedGroups?: boolean;
}

/**
 * Defines the shape of the client export parameters which are generally used when
 * generating rows for downloading templates as excel with pre-populated values
 * specific to current CTC Entity type.
 */
export interface ExportXLSType {
  entity: string;
  items: CTCEntity[];
  columns: ColDef[];
  params: DownloadTemplate;
  augmenter?: Function;
  augmenterArgs?: [any];
}

export interface ExportNonPaginatedClient {
  columns: ColDef[];
  items: CTCEntity[];
  sortOn?: string[];
}

/**
 * Defines the parameters which are used internally when generating a CSV.
 */
interface GenerateCSVParams extends ExportClient {
  items: CTCEntity[];
  lineSeparator?: string;
  columnSeparator?: string;
  augmenterArgs?: any[] | GenericObject;
  title?: string;
  subTitle?: string;
  valueResolver?: (val: any, field: string, item: CTCEntity) => any
}

/**
 * Defines the parameters which are used internally when downloading a CSV.
 */
interface DownloadCSVParams {
  entity: string;
  csv?: string;
  includeTimestamp?: boolean;
  autodownload?: boolean;
}

/**
 * Defines the parameters which are used by clients when exporting a CSV.
 */
export interface ExportCSVParams extends GenerateCSVParams, DownloadCSVParams {
}

/**
 * Defines the default paging parameters used when exporting a collection of entities to a
 * CSV string. Generally, these values should be used and passed to the corresponding API
 * for the initial paging parameters.
 */
export const paging = {
  pageId   : 1,
  pageSize : 100
};

/**
 * @exportCSV
 *
 * Provides the default API which converts a collection of entities to a CSV and automatically
 * downloads a CSV document to the users machine.
 *
 * ```typescript
 *
 *   import exportCSV from '@ion/dls/src/grid/Exporter';
 *
 *   export const exportAssets = (
 *     {entity, sortOn, filterOn, columns, augmenter, includeSubGroups}: ExportParams) => {
 *     return async(dispatch: Function, getState: Function): Promise<any> => {
 *       dispatch({type: EXPORT_ADMIN_ASSETS_REQUEST});
 *       try {
 *         const items = await loadAllAssets({
 *         accountId : getAccountId(getState),
 *         includeSubGroups,
 *         filterOn,
 *         sortOn,
 *         ...paging
 *       });
 *       const csv = exportCSV({
 *         includeTimestamp : true,
           entity,
 *         items,
 *         columns,
 *         augmenter
 *       })
 *       dispatch({type : EXPORT_ADMIN_ASSETS_RESPONSE, csv});
 *     } catch (err) {
 *       dispatch({type: EXPORT_ADMIN_ASSETS_ERROR});
 *       return error(err);
 *     }
 *    };
 *  };
 *
 * ```
 *
 * The exportCVS function can also be used to generate a CSV and postpone downloading via the
 * autodownload argument which, when false, will return the download function which can be
 * called at a later time to initiate the download.
 *
 * ```typescript
 *
 *   const download = exportCSV({
 *     autodownload: false,
 *     augmenter,
 *     columns,
       entity,
 *     items
 *   }) as Function;
 *
 *   // after notifying the user that the download has completed, initiate the download ...
 *   download();
 *
 * ```
 *
 * @param   {ExportCSVParams} param The exporter parameters which are to be used for generating
 *          the completed exported data set, csv conversions, the filename to use, etc.
 * @param   {string} filename An optional filename that will be used when the file is generated
 *          and downloaded to the user's machine.
 * @param   {boolean} includeTimestamp When true, the current timestamp will be included in the
 *          filename (defaults to false)
 * @returns {boolean} true if the download was successful, otherwise false.
 */
export const exportCSV = (params: ExportCSVParams): Function | boolean => {
  const csv = generateCSV(params);
  return downloadCSV({...params, csv});
};

/**
 * @private
 *
 * Given specific export parameters (see ExportParams type), converts a collection of items
 * to a CSV string and returns the result.
 *
 * @param   {ExportParams} param The exporter parameters which are to be used for generating
 *          the completed exported data set, csv conversions, the filename to use, etc.
 * @returns {string} The generated CSV string prepared for subsequent download.
 */
const generateCSV = ({
  items,
  columns,
  lineSeparator,
  columnSeparator,
  augmenterArgs,
  augmenter,
  title,
  subTitle,
  valueResolver
}: GenerateCSVParams): string => {
  const fields          = idMap(columns, 'field');
  const lineDelimiter   = lineSeparator || LINE_SEPARATOR;
  const columnDelimiter = columnSeparator || COLUMN_SEPARATOR;
  let normalized        = [...items];

  const fieldValueResolver = (val, field, item) => {
    val = valueResolver?.(val, field, item) ?? val;
    return !isNil(val) ? `"${val}"` : '""';
  };

  if (augmenter) {
    if (augmenterArgs) {
      normalized = isArray(augmenterArgs) ? augmenter(items, ...(augmenterArgs as []))
        : augmenter({items, ...augmenterArgs});
    } else {
      normalized = augmenter(items);
    }
  }

  let csv = '';

  // adding title and subtitle for csv if passed.
  title && (csv += title + lineDelimiter);
  subTitle && (csv += subTitle + lineDelimiter);

  // adding columns header
  columns.map(({headerName}: ColDef) => {
    if (headerName) {
      csv += headerName;
      csv += columnDelimiter;
    }
  });
  csv += lineDelimiter;
  normalized.forEach((item: CTCEntity) => {
    Object.entries(fields).forEach(([field, definition]) => {
      const def: ColumnDef = definition;
      if (def?.headerName) {
        const val = item[field];
        csv += fieldValueResolver(val, field, item);
        csv += columnDelimiter;
      }
    });
    csv += lineDelimiter;
  });
  return csv;
};

/**
 * @private
 *
 * Manages creating a CSV document from a generated CSV string and automatically downloading
 * the document to the user's machine.
 */
const downloadCSV = ({
  csv,
  entity,
  includeTimestamp = false,
  autodownload = true
}: DownloadCSVParams): Function | boolean => {
  const download = () => {
    let file = translate(`common:export.filenames.${entity}`);
    if (includeTimestamp) {
      const timestamp = moment(new Date()).format('MM-DD-YYYY');
      file = `${file}-${timestamp}`;
    }
    file = `${file}.csv`;
    try {
      const el    = document.createElement('a');
      el.href     = 'data:text/csv;charset=UTF-8,%ef%bb%bf' + encodeURIComponent(csv);
      el.target   = '_blank';
      el.download = file;
      el.click();
      return true;
    } catch (err) {
      error(err);
    }
    return false;
  };
  return autodownload ? download() : download;
};

/**
 * Generates the excel rows for downloading templates with pre-populated values.
 *
 * @param   param
 * @returns The generated rows for downloading templates with
 * pre-populated values.
 */
export const generateRows = ({
  items,
  columns,
  augmenterArgs,
  augmenter
}: ExportCSVParams): CTCEntity[] => {
  const fields = idMap(columns, 'field');
  let normalized;
  const rows: CTCEntity[] = [];

  if (augmenter) {
    normalized = augmenterArgs ? augmenter(items, ...(augmenterArgs as [])) : augmenter(items);
  } else {
    normalized = [...items];
  }
  normalized.forEach((item: CTCEntity) => {
    const row = {};
    Object.entries(fields).forEach(([field, definition]) => {
      const def: ColumnDef = definition;
      if (def?.headerName) {
        const {key} = def;
        const val   = item[field];
        row[key]    = val;
      }
    });
    rows.push(row);
  });

  return rows;
};

/**
 * Defines Device specific fields which require special sanitation prior to being
 * exported to a CSV. Generally, fields which must be retained as strings, such as
 * `iccid` should be added to the map.
 */
const deviceUnsanitizedFields = {
  iccidUI : true
};

/**
 * Provides the value resolver for use by device CSV exports so as to ensure
 * specific value serializations are made prior to exporting to a CSV file.
 *
 * ```typescript
 * import {deviceValueResolver} from '@ion/dls/src/grid/Exporter';
 * ...
 * exportCSV({
 *   valueResolver : valueResolver,
 *   items         : devices
 * });
 * ```
 *
 * @param   val The value to be sanitized prior to export
 * @param   field The field for which the value is provided
 * @param   item A reference to the Device entity for which the field and value
 *          are associated.
 * @returns A specific serialization for the given value if the values is
 *          assigned to a field listed in the pre-defined unsanitized fields
 *          list.
 */
export const deviceValueResolver = (val: any, field: string, item?: CTCEntity) => {
  if (deviceUnsanitizedFields[field] && !isNil(val)) {
    return !isNaN(Number(val)) ? `'${val}'` : val;
  }
  return val;
};

export default exportCSV;
